import type { PublicKey } from '@shared/types';
import { x25519 } from '@noble/curves/ed25519.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256, sha512 } from '@noble/hashes/sha2.js';
import { randomBytes } from '@noble/hashes/utils.js';
import { gcm } from '@noble/ciphers/aes.js';
import {
  base64ToBytes,
  bytesToBase64,
  toArrayBuffer,
  utf8ToBytes,
  bytesToUtf8
} from '../utils/encoding';

const ENCODED_PREFIX_V1 = 'v1:';
const ENCODED_PREFIX_V2 = 'v2:';
const ENCODED_PREFIX_V3 = 'v3:';
const NONCE_LENGTH_V3 = 12;
const V3_SALT_LENGTH = 16;
const V3_KEY_LENGTH = 32;
const CURVE_P = (1n << 255n) - 19n;
const V3_KDF_INFO = utf8ToBytes('videocall:msg:v3:key');
const V3_AAD = utf8ToBytes('videocall:msg:v3');

function mod(a: bigint, m: bigint): bigint {
  const out = a % m;
  return out >= 0n ? out : out + m;
}

function invertMod(a: bigint, m: bigint): bigint | null {
  let t = 0n;
  let newT = 1n;
  let r = m;
  let newR = mod(a, m);

  while (newR !== 0n) {
    const q = r / newR;
    [t, newT] = [newT, t - q * newT];
    [r, newR] = [newR, r - q * newR];
  }

  if (r !== 1n) return null;
  return mod(t, m);
}

function littleEndianToBigInt(bytes: Uint8Array): bigint {
  let out = 0n;
  for (let i = bytes.length - 1; i >= 0; i--) {
    out = (out << 8n) + BigInt(bytes[i]);
  }
  return out;
}

function bigIntToLittleEndian32(value: bigint): Uint8Array {
  const out = new Uint8Array(32);
  let v = mod(value, CURVE_P);
  for (let i = 0; i < 32; i++) {
    out[i] = Number(v & 0xffn);
    v >>= 8n;
  }
  return out;
}

function convertEd25519PublicKeyToX25519(publicKey: Uint8Array): Uint8Array | null {
  if (publicKey.length !== 32) return null;

  const yBytes = publicKey.slice();
  yBytes[31] &= 0x7f;
  const y = littleEndianToBigInt(yBytes);
  if (y >= CURVE_P) return null;

  const one = 1n;
  const denominator = mod(one - y, CURVE_P);
  const invDenominator = invertMod(denominator, CURVE_P);
  if (!invDenominator) return null;

  const u = mod((one + y) * invDenominator, CURVE_P);
  return bigIntToLittleEndian32(u);
}

function convertEd25519SecretKeyToX25519(secretKey: Uint8Array): Uint8Array | null {
  let seed: Uint8Array;
  if (secretKey.length === 64) {
    seed = secretKey.slice(0, 32);
  } else if (secretKey.length === 32) {
    seed = secretKey;
  } else {
    return null;
  }

  const hash = sha512(seed);
  const scalar = hash.slice(0, 32);
  scalar[0] &= 248;
  scalar[31] &= 127;
  scalar[31] |= 64;
  return scalar;
}

function toCurve25519PublicKey(publicKey: PublicKey): Uint8Array | null {
  const raw = base64ToBytes(publicKey.value);
  if (publicKey.algorithm === 'x25519') return raw;
  return convertEd25519PublicKeyToX25519(raw);
}

function toCurve25519SecretKey(
  secretKey: Uint8Array,
  algorithm: PublicKey['algorithm']
): Uint8Array | null {
  if (algorithm === 'x25519') {
    return secretKey.length > 32 ? secretKey.slice(0, 32) : secretKey;
  }
  return convertEd25519SecretKeyToX25519(secretKey);
}

function deriveCurveKeyPair(
  secretKey: Uint8Array,
  secretKeyAlgorithm: PublicKey['algorithm'],
  recipientPublicKey: PublicKey
): { curvePublicKey: Uint8Array; curveSecretKey: Uint8Array } | null {
  const curvePublicKey = toCurve25519PublicKey(recipientPublicKey);
  const curveSecretKey = toCurve25519SecretKey(secretKey, secretKeyAlgorithm);
  if (!curvePublicKey || !curveSecretKey) return null;
  return { curvePublicKey, curveSecretKey };
}

async function deriveSharedSecretNative(
  curveSecretKey: Uint8Array,
  curvePublicKey: Uint8Array
): Promise<Uint8Array | null> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return null;

  try {
    const privateKey = await subtle.importKey(
      'raw',
      toArrayBuffer(curveSecretKey),
      { name: 'X25519' },
      false,
      ['deriveBits']
    );
    const publicKey = await subtle.importKey(
      'raw',
      toArrayBuffer(curvePublicKey),
      { name: 'X25519' },
      false,
      []
    );
    const shared = await subtle.deriveBits(
      {
        name: 'X25519',
        public: publicKey
      },
      privateKey,
      256
    );
    return new Uint8Array(shared);
  } catch {
    return null;
  }
}

async function deriveV3KeyBytes(
  secretKey: Uint8Array,
  secretKeyAlgorithm: PublicKey['algorithm'],
  recipientPublicKey: PublicKey,
  salt: Uint8Array
): Promise<Uint8Array | null> {
  const pair = deriveCurveKeyPair(secretKey, secretKeyAlgorithm, recipientPublicKey);
  if (!pair) return null;

  const nativeSharedSecret = await deriveSharedSecretNative(pair.curveSecretKey, pair.curvePublicKey);
  const sharedSecret = nativeSharedSecret || x25519.getSharedSecret(pair.curveSecretKey, pair.curvePublicKey);

  return hkdf(sha256, sharedSecret, salt, V3_KDF_INFO, V3_KEY_LENGTH);
}

function randomBytesSecure(length: number): Uint8Array {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.getRandomValues) {
    return cryptoApi.getRandomValues(new Uint8Array(length));
  }
  return randomBytes(length);
}

function encryptWithNobleAesGcm(key: Uint8Array, nonce: Uint8Array, plaintext: Uint8Array): Uint8Array {
  return gcm(key, nonce, V3_AAD).encrypt(plaintext);
}

function decryptWithNobleAesGcm(key: Uint8Array, nonce: Uint8Array, encrypted: Uint8Array): Uint8Array {
  return gcm(key, nonce, V3_AAD).decrypt(encrypted);
}

async function encryptWithNativeAesGcm(
  key: Uint8Array,
  nonce: Uint8Array,
  plaintext: Uint8Array
): Promise<Uint8Array | null> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return null;

  try {
    const cryptoKey = await subtle.importKey(
      'raw',
      toArrayBuffer(key),
      { name: 'AES-GCM' },
      false,
      ['encrypt']
    );
    const encrypted = await subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: toArrayBuffer(nonce),
        additionalData: toArrayBuffer(V3_AAD)
      },
      cryptoKey,
      toArrayBuffer(plaintext)
    );
    return new Uint8Array(encrypted);
  } catch {
    return null;
  }
}

async function decryptWithNativeAesGcm(
  key: Uint8Array,
  nonce: Uint8Array,
  encrypted: Uint8Array
): Promise<Uint8Array | null> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return null;

  try {
    const cryptoKey = await subtle.importKey(
      'raw',
      toArrayBuffer(key),
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );
    const decrypted = await subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: toArrayBuffer(nonce),
        additionalData: toArrayBuffer(V3_AAD)
      },
      cryptoKey,
      toArrayBuffer(encrypted)
    );
    return new Uint8Array(decrypted);
  } catch {
    return null;
  }
}

async function deriveV3KeyBytesWithEcdhKey(
  ecdhKey: CryptoKey | null,
  ecdhScalar: Uint8Array,
  recipientPublicKey: PublicKey,
  salt: Uint8Array
): Promise<Uint8Array | null> {
  const recipientCurveBytes = toCurve25519PublicKey(recipientPublicKey);
  if (!recipientCurveBytes) return null;

  // Try WebCrypto X25519 path (available Chrome 133+, Firefox 130+, Safari 15.4+).
  if (ecdhKey) {
    const subtle = globalThis.crypto?.subtle;
    if (subtle) {
      try {
        const recipientKey = await subtle.importKey('raw', toArrayBuffer(recipientCurveBytes), { name: 'X25519' }, false, []);
        const shared = new Uint8Array(await subtle.deriveBits({ name: 'X25519', public: recipientKey }, ecdhKey, 256));
        return hkdf(sha256, shared, salt, V3_KDF_INFO, V3_KEY_LENGTH);
      } catch {
        // fall through to noble
      }
    }
  }

  // Noble fallback: ecdhScalar is the clamped X25519 private scalar.
  const shared = x25519.getSharedSecret(ecdhScalar, recipientCurveBytes);
  return hkdf(sha256, shared, salt, V3_KDF_INFO, V3_KEY_LENGTH);
}

/**
 * Encrypt a message using a pre-computed X25519 key.
 * ecdhKey is used when available (WebCrypto); ecdhScalar is the noble fallback.
 */
export async function encryptMessageAsyncWithKey(
  plaintext: string,
  recipientPublicKey: PublicKey,
  ecdhKey: CryptoKey | null,
  ecdhScalar: Uint8Array
): Promise<string> {
  const salt = randomBytesSecure(V3_SALT_LENGTH);
  const nonce = randomBytesSecure(NONCE_LENGTH_V3);
  const key = await deriveV3KeyBytesWithEcdhKey(ecdhKey, ecdhScalar, recipientPublicKey, salt);
  if (!key) throw new Error('Unable to derive v3 encryption key.');

  const plaintextBytes = utf8ToBytes(plaintext);
  const nativeEncrypted = await encryptWithNativeAesGcm(key, nonce, plaintextBytes);
  const encrypted = nativeEncrypted || encryptWithNobleAesGcm(key, nonce, plaintextBytes);

  const payload = new Uint8Array(salt.length + nonce.length + encrypted.length);
  payload.set(salt, 0);
  payload.set(nonce, salt.length);
  payload.set(encrypted, salt.length + nonce.length);
  return `${ENCODED_PREFIX_V3}${bytesToBase64(payload)}`;
}

export function isEncryptedPayload(ciphertext: string): boolean {
  return ciphertext.startsWith(ENCODED_PREFIX_V1)
    || ciphertext.startsWith(ENCODED_PREFIX_V2)
    || ciphertext.startsWith(ENCODED_PREFIX_V3);
}

export async function encryptMessageAsync(
  plaintext: string,
  recipientPublicKey: PublicKey,
  senderSecretKey: Uint8Array,
  senderPublicKeyAlgorithm: PublicKey['algorithm']
): Promise<string> {
  const salt = randomBytesSecure(V3_SALT_LENGTH);
  const nonce = randomBytesSecure(NONCE_LENGTH_V3);
  const key = await deriveV3KeyBytes(
    senderSecretKey,
    senderPublicKeyAlgorithm,
    recipientPublicKey,
    salt
  );
  if (!key) {
    throw new Error('Unable to derive v3 encryption key.');
  }

  const plaintextBytes = utf8ToBytes(plaintext);
  const nativeEncrypted = await encryptWithNativeAesGcm(key, nonce, plaintextBytes);
  const encrypted = nativeEncrypted || encryptWithNobleAesGcm(key, nonce, plaintextBytes);

  const payload = new Uint8Array(salt.length + nonce.length + encrypted.length);
  payload.set(salt, 0);
  payload.set(nonce, salt.length);
  payload.set(encrypted, salt.length + nonce.length);
  return `${ENCODED_PREFIX_V3}${bytesToBase64(payload)}`;
}

/**
 * Decrypt a message using a pre-computed X25519 key.
 * ecdhKey is used when available (WebCrypto); ecdhScalar is the noble fallback.
 */
export async function decryptMessageAsyncWithKey(
  ciphertext: string,
  senderPublicKey: PublicKey,
  ecdhKey: CryptoKey | null,
  ecdhScalar: Uint8Array
): Promise<string | null> {
  if (!isEncryptedPayload(ciphertext)) return ciphertext;
  if (!ciphertext.startsWith(ENCODED_PREFIX_V3)) return null;

  const payload = base64ToBytes(ciphertext.slice(ENCODED_PREFIX_V3.length));
  if (payload.length <= V3_SALT_LENGTH + NONCE_LENGTH_V3) return null;

  const salt = payload.slice(0, V3_SALT_LENGTH);
  const nonce = payload.slice(V3_SALT_LENGTH, V3_SALT_LENGTH + NONCE_LENGTH_V3);
  const encrypted = payload.slice(V3_SALT_LENGTH + NONCE_LENGTH_V3);

  const key = await deriveV3KeyBytesWithEcdhKey(ecdhKey, ecdhScalar, senderPublicKey, salt);
  if (!key) return null;

  const nativeDecrypted = await decryptWithNativeAesGcm(key, nonce, encrypted);
  const decrypted = nativeDecrypted || (() => {
    try {
      return decryptWithNobleAesGcm(key, nonce, encrypted);
    } catch {
      return null;
    }
  })();
  if (!decrypted) return null;
  return bytesToUtf8(decrypted);
}

export async function decryptMessageAsync(
  ciphertext: string,
  senderPublicKey: PublicKey,
  recipientSecretKey: Uint8Array,
  recipientPublicKeyAlgorithm: PublicKey['algorithm']
): Promise<string | null> {
  if (!isEncryptedPayload(ciphertext)) return ciphertext;

  // Legacy payloads are intentionally no longer supported.
  if (!ciphertext.startsWith(ENCODED_PREFIX_V3)) return null;

  const payload = base64ToBytes(ciphertext.slice(ENCODED_PREFIX_V3.length));
  if (payload.length <= V3_SALT_LENGTH + NONCE_LENGTH_V3) return null;

  const salt = payload.slice(0, V3_SALT_LENGTH);
  const nonce = payload.slice(V3_SALT_LENGTH, V3_SALT_LENGTH + NONCE_LENGTH_V3);
  const encrypted = payload.slice(V3_SALT_LENGTH + NONCE_LENGTH_V3);

  const key = await deriveV3KeyBytes(
    recipientSecretKey,
    recipientPublicKeyAlgorithm,
    senderPublicKey,
    salt
  );
  if (!key) return null;

  const nativeDecrypted = await decryptWithNativeAesGcm(key, nonce, encrypted);
  const decrypted = nativeDecrypted || (() => {
    try {
      return decryptWithNobleAesGcm(key, nonce, encrypted);
    } catch {
      return null;
    }
  })();
  if (!decrypted) return null;
  return bytesToUtf8(decrypted);
}
